/**
 * COLLECTION CLEANUP - User-Facing Data Cleanup Tool
 * 
 * A simpler version of the admin DataCleanupWizard that allows users
 * to clean up their own collection data.
 * 
 * Features:
 * - Placeholder values (????, N/A, unknown, none)
 * - Whitespace issues (leading/trailing, double spaces)
 * - Empty required fields (missing brand/name)
 * - Tag-like data in wrong fields
 * - Potential duplicates (with dismiss option for intentional dupes)
 * - Inconsistent brand names
 * 
 * Accessed from: Settings > Data Management
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import {
  X, ChevronDown, ChevronUp,
  RefreshCw, Eye, EyeOff
} from 'lucide-react';
import type { CollectionItem } from '../types';

interface CleanupIssue {
  id: string;
  type: 'placeholder' | 'whitespace' | 'empty' | 'tags_wrong_field' | 'duplicate' | 'inconsistent_brand';
  itemId: string;
  item: CollectionItem;
  field: string;
  currentValue: string;
  suggestedValue?: string;
  relatedItems?: CollectionItem[]; // For duplicates
  isDismissed?: boolean;
}

interface IssueGroup {
  type: CleanupIssue['type'];
  title: string;
  description: string;
  emoji: string;
  issues: CleanupIssue[];
  canAutoFix: boolean;
}

// Placeholder patterns to detect
const PLACEHOLDER_PATTERNS = [
  /^\?+$/,           // ???? 
  /^n\/?a$/i,        // N/A, n/a, N/a
  /^none$/i,         // none
  /^unknown$/i,      // unknown
  /^tbd$/i,          // TBD
  /^-+$/,            // ---
  /^\.+$/,           // ...
  /^x+$/i,           // xxx
  /^\s*$/,           // whitespace only
];

// Tag keywords for detection
const TAG_KEYWORDS = new Set([
  'shimmer', 'holo', 'holographic', 'glitter', 'matte', 'cream', 'creme', 'jelly',
  'sheer', 'metallic', 'duochrome', 'multichrome', 'magnetic', 'thermal', 'glow',
  'flake', 'flakie', 'topper', 'crelly', 'linear', 'scattered',
  'red', 'blue', 'green', 'purple', 'pink', 'orange', 'yellow', 'black', 'white',
  'gold', 'silver', 'nude', 'coral', 'teal', 'burgundy', 'navy', 'grey', 'gray',
]);

interface CollectionCleanupProps {
  onClose: () => void;
}

export default function CollectionCleanup({ onClose }: CollectionCleanupProps) {
  const [loading, setLoading] = useState(true);
  const [_items, setItems] = useState<CollectionItem[]>([]);
  const [issueGroups, setIssueGroups] = useState<IssueGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [dismissedIssues, setDismissedIssues] = useState<Set<string>>(new Set());
  const [fixingIssue, setFixingIssue] = useState<string | null>(null);
  const [fixingAll, setFixingAll] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [stats, setStats] = useState({ total: 0, clean: 0, issues: 0 });

  // Load collection and analyze
  useEffect(() => {
    loadAndAnalyze();
  }, []);

  const loadAndAnalyze = async () => {
    setLoading(true);
    try {
      const user = getCurrentUser();
      if (!user) return;

      // Load user's items
      const itemsRef = collection(db, 'users', user.uid, 'items');
      const snapshot = await getDocs(itemsRef);
      const loadedItems = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CollectionItem[];
      
      setItems(loadedItems);

      // Load dismissed issues
      const dismissedRef = doc(db, 'users', user.uid, 'settings', 'cleanupDismissals');
      const dismissedDoc = await getDoc(dismissedRef);
      if (dismissedDoc.exists()) {
        setDismissedIssues(new Set(dismissedDoc.data().dismissedIds || []));
      }

      // Analyze for issues
      analyzeItems(loadedItems);
    } catch (error) {
      console.error('Error loading collection:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeItems = (itemsToAnalyze: CollectionItem[]) => {
    const issues: CleanupIssue[] = [];
    const brandVariants: Map<string, CollectionItem[]> = new Map();
    const duplicateKey = (item: CollectionItem) => 
      `${item.brand?.toLowerCase().trim()}-${item.name?.toLowerCase().trim()}`;
    const potentialDupes: Map<string, CollectionItem[]> = new Map();

    itemsToAnalyze.forEach(item => {
      // Skip deleted items
      if (item.isDeleted) return;

      // Check for placeholder values
      ['brand', 'name'].forEach(field => {
        const value = (item as any)[field];
        if (value && PLACEHOLDER_PATTERNS.some(p => p.test(value.trim()))) {
          issues.push({
            id: `placeholder-${item.id}-${field}`,
            type: 'placeholder',
            itemId: item.id,
            item,
            field,
            currentValue: value,
            suggestedValue: '',
          });
        }
      });

      // Check for whitespace issues
      ['brand', 'name', 'colorDescription'].forEach(field => {
        const value = (item as any)[field];
        if (value) {
          const trimmed = value.trim().replace(/\s+/g, ' ');
          if (trimmed !== value) {
            issues.push({
              id: `whitespace-${item.id}-${field}`,
              type: 'whitespace',
              itemId: item.id,
              item,
              field,
              currentValue: value,
              suggestedValue: trimmed,
            });
          }
        }
      });

      // Check for empty required fields
      if (!item.brand || item.brand.trim() === '') {
        issues.push({
          id: `empty-${item.id}-brand`,
          type: 'empty',
          itemId: item.id,
          item,
          field: 'brand',
          currentValue: '',
        });
      }
      if (!item.name || item.name.trim() === '') {
        issues.push({
          id: `empty-${item.id}-name`,
          type: 'empty',
          itemId: item.id,
          item,
          field: 'name',
          currentValue: '',
        });
      }

      // Check for tag-like data in wrong fields
      ['brand', 'name', 'collectionName'].forEach(field => {
        const value = (item as any)[field];
        if (value && looksLikeTags(value)) {
          issues.push({
            id: `tags-${item.id}-${field}`,
            type: 'tags_wrong_field',
            itemId: item.id,
            item,
            field,
            currentValue: value,
          });
        }
      });

      // Track brand variants for inconsistency detection
      if (item.brand) {
        const normalized = item.brand.toLowerCase().trim();
        if (!brandVariants.has(normalized)) {
          brandVariants.set(normalized, []);
        }
        brandVariants.get(normalized)!.push(item);
      }

      // Track potential duplicates
      const key = duplicateKey(item);
      if (key && key !== '-') {
        if (!potentialDupes.has(key)) {
          potentialDupes.set(key, []);
        }
        potentialDupes.get(key)!.push(item);
      }
    });

    // Find inconsistent brand names (same brand spelled differently)
    const brandGroups = groupSimilarBrands(itemsToAnalyze);
    brandGroups.forEach((items, _key) => {
      if (items.length > 1) {
        const uniqueBrands = [...new Set(items.map(i => i.brand))];
        if (uniqueBrands.length > 1) {
          // Find the most common spelling
          const brandCounts = new Map<string, number>();
          items.forEach(i => {
            brandCounts.set(i.brand, (brandCounts.get(i.brand) || 0) + 1);
          });
          const mostCommon = [...brandCounts.entries()].sort((a, b) => b[1] - a[1])[0][0];
          
          items.forEach(item => {
            if (item.brand !== mostCommon) {
              issues.push({
                id: `brand-${item.id}`,
                type: 'inconsistent_brand',
                itemId: item.id,
                item,
                field: 'brand',
                currentValue: item.brand,
                suggestedValue: mostCommon,
                relatedItems: items.filter(i => i.id !== item.id),
              });
            }
          });
        }
      }
    });

    // Find duplicates
    potentialDupes.forEach((dupeItems, _key) => {
      if (dupeItems.length > 1) {
        // Only flag the 2nd, 3rd, etc. occurrences
        dupeItems.slice(1).forEach(item => {
          issues.push({
            id: `dupe-${item.id}`,
            type: 'duplicate',
            itemId: item.id,
            item,
            field: 'name',
            currentValue: `${item.brand} - ${item.name}`,
            relatedItems: dupeItems.filter(i => i.id !== item.id),
          });
        });
      }
    });

    // Group issues by type
    const groups: IssueGroup[] = ([
      {
        type: 'placeholder',
        title: 'Placeholder Values',
        description: 'Values like "????", "N/A", or "unknown" that should be cleared.',
        emoji: '🗑️',
        issues: issues.filter(i => i.type === 'placeholder'),
        canAutoFix: true,
      },
      {
        type: 'whitespace',
        title: 'Whitespace Issues',
        description: 'Extra spaces at the start/end or double spaces in text.',
        emoji: '✨',
        issues: issues.filter(i => i.type === 'whitespace'),
        canAutoFix: true,
      },
      {
        type: 'empty',
        title: 'Missing Required Info',
        description: 'Items missing brand or name.',
        emoji: '⚠️',
        issues: issues.filter(i => i.type === 'empty'),
        canAutoFix: false,
      },
      {
        type: 'tags_wrong_field',
        title: 'Tags in Wrong Field',
        description: 'Comma-separated lists that look like tags in brand/name fields.',
        emoji: '🏷️',
        issues: issues.filter(i => i.type === 'tags_wrong_field'),
        canAutoFix: false,
      },
      {
        type: 'inconsistent_brand',
        title: 'Inconsistent Brand Names',
        description: 'Same brand spelled differently (e.g., "ILNP" vs "I Love Nail Polish").',
        emoji: '🔤',
        issues: issues.filter(i => i.type === 'inconsistent_brand'),
        canAutoFix: true,
      },
      {
        type: 'duplicate',
        title: 'Potential Duplicates',
        description: 'Items with the same brand and name. Some may be intentional!',
        emoji: '👯',
        issues: issues.filter(i => i.type === 'duplicate'),
        canAutoFix: false,
      },
    ] as IssueGroup[]).filter(g => g.issues.length > 0);

    setIssueGroups(groups);
    
    const totalIssues = issues.length;
    setStats({
      total: itemsToAnalyze.filter(i => !i.isDeleted).length,
      clean: itemsToAnalyze.filter(i => !i.isDeleted).length - new Set(issues.map(i => i.itemId)).size,
      issues: totalIssues,
    });
  };

  // Check if text looks like comma-separated tags
  const looksLikeTags = (value: string): boolean => {
    if (!value || !value.includes(',')) return false;
    
    const items = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
    if (items.length < 2) return false;
    
    // Check if most items are short (1-3 words)
    const shortItems = items.filter(item => item.split(/\s+/).length <= 3);
    const mostAreShort = shortItems.length >= items.length * 0.7;
    
    // Check for tag keywords
    const tagMatches = items.filter(item => TAG_KEYWORDS.has(item.toLowerCase())).length;
    const hasTagKeywords = tagMatches >= 2 || tagMatches >= items.length * 0.3;
    
    return mostAreShort && (hasTagKeywords || items.length >= 3);
  };

  // Group similar brand names (for inconsistency detection)
  const groupSimilarBrands = (items: CollectionItem[]): Map<string, CollectionItem[]> => {
    const groups = new Map<string, CollectionItem[]>();
    
    // Common brand abbreviations/variations
    const normalizations: [RegExp, string][] = [
      [/\s+(nail\s*)?(polish|lacquer|cosmetics?|beauty)$/i, ''],
      [/^(the\s+)/i, ''],
      [/['\-\s\.]+/g, ''],
    ];
    
    const normalize = (brand: string): string => {
      let result = brand.toLowerCase().trim();
      normalizations.forEach(([pattern, replacement]) => {
        result = result.replace(pattern, replacement);
      });
      return result;
    };
    
    items.forEach(item => {
      if (!item.brand || item.isDeleted) return;
      const key = normalize(item.brand);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    });
    
    return groups;
  };

  // Toggle group expansion
  const toggleGroup = (type: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // Dismiss an issue (keep as-is)
  const dismissIssue = async (issueId: string) => {
    const user = getCurrentUser();
    if (!user) return;

    const newDismissed = new Set(dismissedIssues);
    newDismissed.add(issueId);
    setDismissedIssues(newDismissed);

    // Save to Firestore
    try {
      const dismissedRef = doc(db, 'users', user.uid, 'settings', 'cleanupDismissals');
      await setDoc(dismissedRef, { dismissedIds: [...newDismissed] }, { merge: true });
    } catch (error) {
      console.error('Error saving dismissal:', error);
    }
  };

  // Undismiss an issue
  const undismissIssue = async (issueId: string) => {
    const user = getCurrentUser();
    if (!user) return;

    const newDismissed = new Set(dismissedIssues);
    newDismissed.delete(issueId);
    setDismissedIssues(newDismissed);

    // Save to Firestore
    try {
      const dismissedRef = doc(db, 'users', user.uid, 'settings', 'cleanupDismissals');
      await setDoc(dismissedRef, { dismissedIds: [...newDismissed] }, { merge: true });
    } catch (error) {
      console.error('Error saving dismissal:', error);
    }
  };

  // Fix a single issue
  const fixIssue = async (issue: CleanupIssue) => {
    const user = getCurrentUser();
    if (!user) return;

    setFixingIssue(issue.id);
    try {
      const itemRef = doc(db, 'users', user.uid, 'items', issue.itemId);
      
      let updateData: any = {};
      
      if (issue.type === 'placeholder') {
        // Clear the placeholder value
        updateData[issue.field] = '';
      } else if (issue.type === 'whitespace') {
        // Fix whitespace
        updateData[issue.field] = issue.suggestedValue;
      } else if (issue.type === 'inconsistent_brand') {
        // Standardize brand name
        updateData[issue.field] = issue.suggestedValue;
      }

      await updateDoc(itemRef, updateData);
      
      // Refresh data
      await loadAndAnalyze();
    } catch (error) {
      console.error('Error fixing issue:', error);
    } finally {
      setFixingIssue(null);
    }
  };

  // Fix all issues in a group
  const fixAllInGroup = async (group: IssueGroup) => {
    const user = getCurrentUser();
    if (!user) return;

    setFixingAll(group.type);
    try {
      const issuestoFix = group.issues.filter(i => !dismissedIssues.has(i.id));
      
      for (const issue of issuestoFix) {
        const itemRef = doc(db, 'users', user.uid, 'items', issue.itemId);
        
        let updateData: any = {};
        
        if (issue.type === 'placeholder') {
          updateData[issue.field] = '';
        } else if (issue.type === 'whitespace') {
          updateData[issue.field] = issue.suggestedValue;
        } else if (issue.type === 'inconsistent_brand') {
          updateData[issue.field] = issue.suggestedValue;
        }

        if (Object.keys(updateData).length > 0) {
          await updateDoc(itemRef, updateData);
        }
      }
      
      // Refresh data
      await loadAndAnalyze();
    } catch (error) {
      console.error('Error fixing issues:', error);
    } finally {
      setFixingAll(null);
    }
  };

  // Get visible issues (accounting for dismissed)
  const getVisibleIssues = (issues: CleanupIssue[]) => {
    if (showDismissed) return issues;
    return issues.filter(i => !dismissedIssues.has(i.id));
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="glass-card p-8 text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-glass-secondary font-body">Analyzing your collection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="glass-modal w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-600 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-glass font-heading">🧹 Clean Up Collection</h2>
            <p className="text-glass-secondary text-sm mt-1 font-body">
              Find and fix data quality issues in your collection
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-600/60 rounded-full transition-default text-glass-secondary hover:text-glass"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="px-6 py-4 bg-slate-800/40 border-b border-slate-600">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-glass">{stats.total}</div>
              <div className="text-xs text-glass-secondary font-body">Total Items</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">{stats.clean}</div>
              <div className="text-xs text-glass-secondary font-body">Clean Items</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">{stats.issues - dismissedIssues.size}</div>
              <div className="text-xs text-glass-secondary font-body">Issues to Review</div>
            </div>
          </div>
        </div>

        {/* Toggle for showing dismissed */}
        {dismissedIssues.size > 0 && (
          <div className="px-6 py-2 bg-slate-800/20 border-b border-slate-700">
            <button
              onClick={() => setShowDismissed(!showDismissed)}
              className="text-sm text-glass-secondary hover:text-glass flex items-center gap-2 font-body"
            >
              {showDismissed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showDismissed ? 'Hide' : 'Show'} {dismissedIssues.size} dismissed issue{dismissedIssues.size !== 1 ? 's' : ''}
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {issueGroups.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">✨</div>
              <h3 className="text-xl font-bold text-glass mb-2 font-heading">Your Collection is Clean!</h3>
              <p className="text-glass-secondary font-body">No data quality issues found.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {issueGroups.map(group => {
                const visibleIssues = getVisibleIssues(group.issues);
                const dismissedCount = group.issues.length - visibleIssues.length;
                
                return (
                  <div key={group.type} className="bg-slate-800/40 rounded-lg border border-slate-600 overflow-hidden">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.type)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-700/40 transition-default"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{group.emoji}</span>
                        <div className="text-left">
                          <div className="font-semibold text-glass font-body">
                            {group.title}
                            <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded-full">
                              {visibleIssues.length} item{visibleIssues.length !== 1 ? 's' : ''}
                            </span>
                            {dismissedCount > 0 && showDismissed && (
                              <span className="ml-2 px-2 py-0.5 bg-slate-600/50 text-slate-400 text-xs rounded-full">
                                {dismissedCount} dismissed
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-glass-secondary font-body">{group.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {group.canAutoFix && visibleIssues.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              fixAllInGroup(group);
                            }}
                            disabled={fixingAll === group.type}
                            className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-default font-body disabled:opacity-50"
                          >
                            {fixingAll === group.type ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              'Fix All'
                            )}
                          </button>
                        )}
                        {expandedGroups.has(group.type) ? (
                          <ChevronUp className="w-5 h-5 text-glass-secondary" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-glass-secondary" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Issues */}
                    {expandedGroups.has(group.type) && (
                      <div className="border-t border-slate-600">
                        {(showDismissed ? group.issues : visibleIssues).map(issue => (
                          <div 
                            key={issue.id} 
                            className={`p-4 border-b border-slate-700 last:border-b-0 ${
                              dismissedIssues.has(issue.id) ? 'opacity-50 bg-slate-800/20' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-glass font-body">
                                  {issue.item.brand} - {issue.item.name}
                                </div>
                                <div className="text-sm text-glass-secondary mt-1 font-body">
                                  <span className="text-amber-400">{issue.field}:</span>{' '}
                                  <span className="font-mono bg-slate-700/50 px-1 rounded">
                                    "{issue.currentValue || '(empty)'}"
                                  </span>
                                  {issue.suggestedValue !== undefined && (
                                    <>
                                      {' → '}
                                      <span className="font-mono bg-green-900/30 text-green-400 px-1 rounded">
                                        "{issue.suggestedValue || '(clear)'}"
                                      </span>
                                    </>
                                  )}
                                </div>
                                {issue.relatedItems && issue.relatedItems.length > 0 && (
                                  <div className="text-xs text-glass-muted mt-2 font-body">
                                    Related: {issue.relatedItems.slice(0, 3).map(r => `${r.brand} - ${r.name}`).join(', ')}
                                    {issue.relatedItems.length > 3 && ` +${issue.relatedItems.length - 3} more`}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {dismissedIssues.has(issue.id) ? (
                                  <button
                                    onClick={() => undismissIssue(issue.id)}
                                    className="px-3 py-1.5 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-500 transition-default font-body"
                                  >
                                    Restore
                                  </button>
                                ) : (
                                  <>
                                    {group.canAutoFix && (
                                      <button
                                        onClick={() => fixIssue(issue)}
                                        disabled={fixingIssue === issue.id}
                                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-default font-body disabled:opacity-50"
                                      >
                                        {fixingIssue === issue.id ? (
                                          <RefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                          'Fix'
                                        )}
                                      </button>
                                    )}
                                    <button
                                      onClick={() => dismissIssue(issue.id)}
                                      className="px-3 py-1.5 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-500 transition-default font-body"
                                      title="Keep as-is (dismiss this issue)"
                                    >
                                      Keep
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-600 bg-slate-800/40">
          <div className="flex justify-between items-center">
            <p className="text-sm text-glass-secondary font-body">
              💡 Dismissed items won't be shown again until you restore them.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500 transition-default font-body"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
